import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Crown, ArrowRight, HelpCircle } from 'lucide-react';
import { SEO } from '@/components/SEO';

export default function Pricing() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Free & Pro Invoice Generator Plans | QuickFreeInvoice" 
        description="Compare free & Pro invoicing plans. Create unlimited free PDF invoices or upgrade to Pro for $5/mo to unlock Word exports and client view tracking." 
        keywords="free vs pro invoice software, affordable billing software, pro invoice generator, unlimited PDF invoices, small business billing plans, low cost invoice generator, word invoice export"
      />
      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center max-w-4xl">
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-muted-foreground mb-8">
            Start free, upgrade when you need more. No hidden fees, cancel anytime.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Check className="h-4 w-4 text-green-500" />
            <span>No credit card required for free plan</span>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Free Plan */}
            <Card className="relative">
              <CardHeader>
                <CardTitle className="text-2xl">Free</CardTitle>
                <CardDescription>Perfect for trying out the service</CardDescription>
                <div className="mt-4">
                  <span className="text-5xl font-bold">$0</span>
                  <span className="text-muted-foreground text-lg">/month</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Unlimited invoice & quotation downloads</div>
                      <div className="text-sm text-muted-foreground">Download as many PDFs as you need</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Manual business entry each time</div>
                      <div className="text-sm text-muted-foreground">Enter details for each invoice</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Manual customer entry each time</div>
                      <div className="text-sm text-muted-foreground">No saved customer profiles</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Manual item entry each time</div>
                      <div className="text-sm text-muted-foreground">No saved inventory & services database</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Save unlimited invoices</div>
                      <div className="text-sm text-muted-foreground">Store all your invoices online</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">PDF export</div>
                      <div className="text-sm text-muted-foreground">Professional invoice downloads</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Quotation to Invoice Conversion</div>
                      <div className="text-sm text-muted-foreground">Convert quotations into invoices in one click (manual entry details)</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Standard support</div>
                      <div className="text-sm text-muted-foreground">Email support within 48 hours</div>
                    </div>
                  </li>
                </ul>
                <Link to="/register" className="block">
                  <Button variant="outline" className="w-full" size="lg">
                    Get Started Free
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Pro Plan */}
            <Card className="border-2 border-primary relative shadow-lg">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <Badge className="px-4 py-1 flex items-center gap-1 text-sm">
                  <Crown className="h-4 w-4" />
                  Most Popular
                </Badge>
              </div>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  Pro
                  <Crown className="h-5 w-5 text-yellow-500" />
                </CardTitle>
                <CardDescription>For professionals and growing businesses</CardDescription>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-2xl text-muted-foreground line-through font-semibold">$15</span>
                  <span className="text-5xl font-bold text-slate-900">$5</span>
                  <span className="text-muted-foreground text-lg">/month</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Unlimited invoice & quotation downloads</div>
                      <div className="text-sm text-muted-foreground">Download as many invoices & quotations as you need</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Save unlimited business profiles</div>
                      <div className="text-sm text-muted-foreground">Quick access to your business info</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Business Logo Upload & Branding</div>
                      <div className="text-sm text-muted-foreground">Upload your brand logo and place it on your invoices & quotations</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Save unlimited customer profiles</div>
                      <div className="text-sm text-muted-foreground">Never re-enter customer details</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Save unlimited inventory items & services</div>
                      <div className="text-sm text-muted-foreground">Search by name or code to auto-fill invoices</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Save unlimited invoices</div>
                      <div className="text-sm text-muted-foreground">Complete invoice history</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">PDF Export</div>
                      <div className="text-sm text-muted-foreground">Professional invoice downloads</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Word Document Export</div>
                      <div className="text-sm text-muted-foreground">Download editable MS Word invoices styled with custom templates</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Quotation to Invoice Conversion</div>
                      <div className="text-sm text-muted-foreground">Convert quotations into invoices in one click (with saved profile auto-fill)</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Predefined tax rates setup</div>
                      <div className="text-sm text-muted-foreground">Save predefined tax rates (e.g. VAT, GST, SST) for one-click auto-fill</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Unique direct sharing links</div>
                      <div className="text-sm text-muted-foreground">Share unique invoice & quotation URLs for clients to view, print, and download directly</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Client view tracking (read receipts)</div>
                      <div className="text-sm text-muted-foreground">See when and how many times your client viewed their shared invoice link</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Direct online payment links (Stripe & PayPal)</div>
                      <div className="text-sm text-muted-foreground">Embed Stripe or PayPal.me links on invoices — clients can pay with one click from their shared view</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Priority email support</div>
                      <div className="text-sm text-muted-foreground">Get help within 24 hours</div>
                    </div>
                  </li>
                </ul>
                <Link to="/register" className="block">
                  <Button className="w-full" size="lg">
                    Upgrade to Pro Plan
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-3xl font-bold text-center mb-12">
            Feature Comparison
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2">
                  <th className="text-left py-4 px-4 font-semibold">Feature</th>
                  <th className="text-center py-4 px-4 font-semibold">Free</th>
                  <th className="text-center py-4 px-4 font-semibold bg-primary/5">
                    <div className="flex items-center justify-center gap-2">
                      Pro
                      <Crown className="h-4 w-4 text-yellow-500" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-4 px-4">Invoice &amp; Quotation Downloads</td>
                  <td className="text-center py-4 px-4">Unlimited</td>
                  <td className="text-center py-4 px-4 bg-primary/5 font-medium">Unlimited</td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4">Business Profiles</td>
                  <td className="text-center py-4 px-4">Manual entry</td>
                  <td className="text-center py-4 px-4 bg-primary/5 font-medium">Unlimited saved</td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4">Business Logo Upload</td>
                  <td className="text-center py-4 px-4">No (Disabled)</td>
                  <td className="text-center py-4 px-4 bg-primary/5 font-medium">Yes (With auto-scaling & export support)</td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4">Customer Profiles</td>
                  <td className="text-center py-4 px-4">Manual entry</td>
                  <td className="text-center py-4 px-4 bg-primary/5 font-medium">Unlimited saved</td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4">Inventory Items & Services</td>
                  <td className="text-center py-4 px-4">Manual entry</td>
                  <td className="text-center py-4 px-4 bg-primary/5 font-medium">Unlimited saved (search &amp; auto-fill)</td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4">Quotations &amp; Estimates</td>
                  <td className="text-center py-4 px-4">Manual entry only</td>
                  <td className="text-center py-4 px-4 bg-primary/5 font-medium">Profile &amp; inventory auto-fill</td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4">Quotation-to-Invoice Conversion</td>
                  <td className="text-center py-4 px-4">One-click (manual details)</td>
                  <td className="text-center py-4 px-4 bg-primary/5 font-medium">One-click (with profile auto-fill)</td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4">Predefined Taxes Setup</td>
                  <td className="text-center py-4 px-4">Manual entry</td>
                  <td className="text-center py-4 px-4 bg-primary/5 font-medium">Unlimited predefined rates (auto-fill)</td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4">Unique Sharing Links</td>
                  <td className="text-center py-4 px-4">No (Manual PDF)</td>
                  <td className="text-center py-4 px-4 bg-primary/5 font-medium">Direct share URLs (view, print, download)</td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4">Client View Tracking</td>
                  <td className="text-center py-4 px-4"><span className="text-muted-foreground">-</span></td>
                  <td className="text-center py-4 px-4 bg-primary/5 font-medium">Read receipts with timestamp & device info</td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4">Online Payment Links (Stripe / PayPal)</td>
                  <td className="text-center py-4 px-4"><span className="text-muted-foreground">-</span></td>
                  <td className="text-center py-4 px-4 bg-primary/5 font-medium">Embed Stripe & PayPal links on invoices</td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4">Invoice Storage</td>
                  <td className="text-center py-4 px-4">
                    <Check className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="text-center py-4 px-4 bg-primary/5">
                    <Check className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4">PDF Export</td>
                  <td className="text-center py-4 px-4">
                    <Check className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="text-center py-4 px-4 bg-primary/5">
                    <Check className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4">Word Document Export</td>
                  <td className="text-center py-4 px-4">
                    <span className="text-muted-foreground">-</span>
                  </td>
                  <td className="text-center py-4 px-4 bg-primary/5">
                    <Check className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4">Support</td>
                  <td className="text-center py-4 px-4">Standard (48h)</td>
                  <td className="text-center py-4 px-4 bg-primary/5 font-medium">Priority (24h)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <HelpCircle className="h-12 w-12 text-primary mx-auto mb-4" />
            <h2 className="font-heading text-3xl font-bold mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Do I need a credit card to start?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  No! You can start using QuickFreeInvoice completely free without entering any payment information. 
                  Only upgrade to Pro when you're ready.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Can I cancel my Pro subscription anytime?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Yes, you can cancel your Pro subscription at any time with no questions asked. You'll retain 
                  Pro access until the end of your current billing period.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What is the difference between the Free and Pro plans?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Both plans offer unlimited invoice and quotation creations and PDF downloads. The Pro plan unlocks productivity automation features like saving unlimited Business, Customer, and Inventory & Services profiles (to search and auto-fill your invoices in seconds), business logo upload and custom branding, unique client sharing links, client view tracking (read receipts), direct online payment links (Stripe &amp; PayPal), priority support, and professional Word document exports.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Is there a refund policy?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We offer a 7-day money-back guarantee for your first Pro subscription purchase. See our{' '}
                  <Link to="/refund" className="text-primary hover:underline">
                    Refund Policy
                  </Link>
                  {' '}for complete details.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How secure is my data?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We use industry-standard encryption and security practices. All data is stored securely on 
                  Firebase servers with regular backups. We never share your data with third parties.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Can I upgrade or downgrade at any time?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Yes! You can upgrade to Pro instantly at any time. If you downgrade, you'll keep Pro features 
                  until the end of your current billing period, then automatically switch to the Free plan.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container mx-auto text-center max-w-3xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Ready to get started?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Create your first professional invoice in minutes
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
                Start Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white/10">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
